import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { authMe } from "@/lib/afmApi";

const TOKEN_KEY = "sona_session_token";

const ERROR_MESSAGES = {
  not_registered: "Esta cuenta de Google no existe en SONA. Regístrate primero en la app SONA para acceder.",
  state: "La sesión de autenticación expiró o es inválida. Intenta de nuevo.",
  token_exchange: "No se pudo validar con Google. Intenta de nuevo.",
  no_access_token: "Google no devolvió un token. Intenta de nuevo.",
  userinfo: "No se pudo obtener tu información de Google.",
  unverified: "Tu correo de Google no está verificado.",
  google_unreachable: "No se pudo conectar con Google. Intenta más tarde.",
  missing_code: "Autenticación incompleta. Intenta de nuevo.",
};

export default function AuthCallback() {
  const { signInWith } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const raw = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
    const params = new URLSearchParams(raw);
    const token = params.get("token");
    const err = params.get("error");
    window.history.replaceState(null, "", window.location.pathname);

    if (err) { setError(ERROR_MESSAGES[err] || "No se pudo iniciar sesión."); return; }
    if (!token) { setError("No se recibió la sesión. Intenta iniciar sesión de nuevo."); return; }

    (async () => {
      try {
        localStorage.setItem(TOKEN_KEY, token);
        const user = await authMe(token);
        signInWith(user);
        navigate("/dashboard", { replace: true });
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
        setError("No se pudo validar la sesión. Intenta de nuevo.");
      }
    })();
  }, [navigate, signInWith]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-6" data-testid="auth-callback-error">
        <div className="max-w-sm text-center">
          <h3 className="font-serif text-2xl font-bold text-black">No se pudo iniciar sesión</h3>
          <p className="mt-3 text-sm text-slate-600">{error}</p>
          <Link to="/login" data-testid="auth-callback-retry" className="mt-6 inline-block rounded-lg bg-black px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">Volver a intentar</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-white" data-testid="auth-callback">
      <div className="flex flex-col items-center gap-4">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-black" />
        <p className="text-sm text-slate-500">Iniciando sesión…</p>
      </div>
    </div>
  );
}
